<script setup lang="ts">
import { computed } from 'vue';
import { gaiaFonts, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  width: {
    type: String,
    default: '252px',
  },
  /** Render the collapse toggle on the rail border. */
  collapsible: {
    type: Boolean,
    default: false,
  },
  /** v-model:collapsed — the 64px icon-only rail state (visual only). */
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:collapsed': [collapsed: boolean] }>();

const isCollapsed = computed(() => props.collapsible && props.collapsed);

const bg = gaiaNeutral.surfaceSoft;
const border = gaiaNeutral.borderMuted;
const font = gaiaFonts.nav;
const surface = gaiaNeutral.surface;
const toggleColor = gaiaNeutral.textFaint;
const ink = gaiaNeutral.ink;
</script>

<template>
  <!-- The production sidebar shell from mock 5a — a mount point like
       GaiaPanel: workspace switcher (#header), #search, nav rows (default
       slot) and #footer are all composed in by the page (GaiaAvatar,
       GaiaSearchInput, GaiaNavItem, GaiaSectionHeader, GaiaProgressBar).
       `collapsible` + v-model:collapsed give the 64px icon-only rail; the
       collapsed look is purely CSS in this file (see below), no state is
       shared with the slotted components. -->
  <aside
    class="gaia-sidebar"
    :class="{ 'gaia-sidebar--collapsed': isCollapsed }"
    :style="{ width: isCollapsed ? '64px' : props.width }"
  >
    <button
      v-if="props.collapsible"
      type="button"
      class="collapse-toggle"
      :aria-label="props.collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="emit('update:collapsed', !props.collapsed)"
    >
      <svg
        class="toggle-chev"
        :class="{ collapsed: isCollapsed }"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      >
        <path d="M7.5 2.5 4 6l3.5 3.5" />
      </svg>
    </button>
    <div v-if="$slots.header || $slots.search" class="top">
      <slot name="header" />
      <slot name="search" />
    </div>
    <nav class="nav">
      <slot />
    </nav>
    <div v-if="$slots.footer" class="footer">
      <slot name="footer" />
    </div>
  </aside>
</template>

<style scoped>
.gaia-sidebar {
  position: relative;
  /* The collapse toggle deliberately hangs 12px past the right border, so the
     rail has to paint above whatever sits next to it in the app shell. This is
     2, not 1, because naive stamps `z-index: 1` on .n-layout-sider: a page
     whose content opens with its own sider (the wizard's step rail) would
     otherwise tie with us and win on DOM order, clipping the toggle in half. */
  z-index: 2;
  flex: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: v-bind(bg);
  border-right: 1px solid v-bind(border);
  /* Set here so slotted content inherits the nav family too. */
  font-family: v-bind(font);
  transition: width 0.2s ease;
}

/* The collapse toggle straddles the rail border, vertically centered like
   the app's current n-layout-sider trigger (no naive primitive here). */
.collapse-toggle {
  position: absolute;
  top: 50%;
  margin-top: -12px;
  right: -12px;
  z-index: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid v-bind(border);
  border-radius: 50%;
  background: v-bind(surface);
  color: v-bind(toggleColor);
  cursor: pointer;
  padding: 0;
}

.collapse-toggle:hover {
  color: v-bind(ink);
  border-color: v-bind(ink);
}

.toggle-chev {
  transition: transform 0.2s ease;
}

.toggle-chev.collapsed {
  transform: rotate(180deg);
}

/* .top and .footer stay CSS: the collapsed rail adds `align-items: center` to
   both, and an n-flex would write alignment inline where that can't reach it. */
.top {
  padding: 14px 14px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 10px 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.footer {
  border-top: 1px solid v-bind(border);
  padding: 12px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Collapsed 64px rail — design only. The slotted Gaia components keep their
   markup; this shell hides their text parts and centers what's left. */
.gaia-sidebar--collapsed .top {
  padding: 14px 10px 0;
  align-items: center;
}

.gaia-sidebar--collapsed .nav {
  padding: 10px 8px 0;
}

.gaia-sidebar--collapsed .footer {
  padding: 12px 10px 10px;
  align-items: center;
}

.gaia-sidebar--collapsed .top :deep(.n-input),
.gaia-sidebar--collapsed :deep(.gaia-nav-item .label),
.gaia-sidebar--collapsed :deep(.gaia-nav-item .count),
.gaia-sidebar--collapsed :deep(.gaia-nav-item .chev),
.gaia-sidebar--collapsed :deep(.gaia-nav-item.child),
.gaia-sidebar--collapsed :deep(.gaia-section-header),
.gaia-sidebar--collapsed :deep(.gaia-progress-bar),
.gaia-sidebar--collapsed :deep(.gaia-workspace-switcher .name),
.gaia-sidebar--collapsed :deep(.gaia-workspace-switcher .chevrons),
.gaia-sidebar--collapsed :deep(.gaia-user-row .texts),
.gaia-sidebar--collapsed :deep(.gaia-user-row .trailing) {
  display: none;
}

.gaia-sidebar--collapsed :deep(.gaia-nav-item) {
  justify-content: center;
  padding: 9px 0;
}

.gaia-sidebar--collapsed :deep(.gaia-workspace-switcher),
.gaia-sidebar--collapsed :deep(.gaia-user-row) {
  justify-content: center;
  padding: 4px 0;
}

/* Collapsed: the avatar button shrinks to its content so it centers. */
.gaia-sidebar--collapsed :deep(.gaia-user-row .main) {
  flex: none;
  padding: 4px 2px;
}
</style>
